import { jwtDecode } from "jwt-decode";
import { removeAxiosAuth, setAxiosAuth } from "@/utils/api";
import Cookies from "js-cookie";
import { AUTH_COOKIE_EXPIRES_DAYS } from "@/constants/common";

const isDevMode = process.env.REACT_APP_APP_ENV === "DEV";

/**
 * Sets the access and refresh tokens in cookies and updates the Axios authentication configuration.
 *
 * @param {string} accessToken - The access token to set.
 * @param {string} refreshToken - The refresh token to set.
 */
export const setToken = (accessToken: string, refreshToken: string): void => {
  const options: Cookies.CookieAttributes = { expires: AUTH_COOKIE_EXPIRES_DAYS };

  // Local development runs over plain http, where a `secure` cookie is dropped.
  if (!isDevMode) {
    options.secure = true;
    options.sameSite = "strict";
  }

  Cookies.set("match_token", accessToken, options);
  Cookies.set("refresh_token", refreshToken, options);
  setAxiosAuth(); // Updates Axios instance with new tokens for authentication
};

/**
 * Removes the access and refresh tokens from cookies and updates the Axios authentication configuration.
 */
export const removeToken = (): void => {
  Cookies.remove("match_token");
  Cookies.remove("refresh_token");
  removeAxiosAuth(); // Updates Axios instance to remove authentication
};

/**
 * Verifies if a given token is still valid based on its expiration time.
 *
 * @param {string} token - The token to verify.
 * @returns {boolean} - `true` if the token is still valid, `false` otherwise.
 */
export const verifyToken = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    // Check if the token's expiration time is greater than the current time
    return decoded.exp > new Date().getTime() / 1000;
  } catch (e) {
    // Return false if decoding fails
    return false;
  }
};

/**
 * Decode a JWT payload WITHOUT verifying its signature.
 * Frontend port of the backend `decodeJwtPayload` (jwt-claims.util.ts).
 */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    return decoded && typeof decoded === "object" ? decoded : null;
  } catch {
    return null;
  }
};

/**
 * Extract claim keys from a JWT into a string map.
 * Frontend port of the backend `claimExactKeys` (jwt-claims.util.ts): object/nullish
 * claims are skipped, everything else is coerced to string.
 *
 * @example
 * claimExactKeys(accessToken, ["sub", "organizationId"]) // => { sub: "...", organizationId: "..." }
 */
export const claimExactKeys = <K extends string>(token: string, keys: readonly K[]): Partial<Record<K, string>> => {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) return {};

    const claimed: Partial<Record<K, string>> = {};
    for (const key of keys) {
      const value = payload[key];
      if (value == null || typeof value === "object") continue;
      claimed[key] = String(value);
    }
    return claimed;
  } catch {
    return {};
  }
};
